import { CheckCircle2, CircleDashed, ExternalLink, XCircle } from "lucide-react";
import { relColor, lbl } from "@/pages/candidates/experience/CareerGantt";
import { cn } from "@/lib/utils";

export function RoleCards({ roles }) {
  return (
    <section className="space-y-3" data-tour="pt-role-cards" data-testid="role-cards">
      {[...roles].reverse().map((r) => (
        <article key={r.index} className="panel space-y-2 p-4" data-testid={`role-card-${r.index}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div><p className="font-medium text-ink">{r.title}</p><p className="text-xs text-ink-muted">{r.company} · {lbl(r.start)} – {r.is_current ? "now" : lbl(r.end)} · {r.months} months{r.dates_incomplete ? " · dates incomplete" : ""}</p></div>
            <span className="font-display text-xl font-semibold tabular-nums" style={{ color: relColor(r.relevance) }} data-testid={`role-relevance-${r.index}`}>{r.relevance ?? "—"}</span>
          </div>
          <p className="text-sm text-ink-secondary">{r.rationale}</p>
          {r.matched_responsibilities.length > 0 && <div className="flex flex-wrap gap-1.5">{r.matched_responsibilities.map((m) => <span key={m} className="rounded-full bg-brand-tint px-2.5 py-0.5 text-[11px] text-brand">{m}</span>)}</div>}
          {r.evidence.map((q) => <blockquote key={q} className="border-l-2 border-brand/40 pl-3 text-xs italic text-ink-secondary">"{q}"</blockquote>)}
        </article>
      ))}
    </section>
  );
}

export function GapsPanel({ e }) {
  const p = e.progression;
  return (
    <section className="panel space-y-3 p-5" data-tour="pt-gaps-panel" data-testid="gaps-panel">
      <h3 className="text-base font-semibold">Progression, gaps and overlaps</h3>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <dt className="text-ink-muted">Trend</dt><dd className="capitalize" data-testid="progression-trend">{p.trend}</dd>
        <dt className="text-ink-muted">Promotions / step-ups</dt><dd>{p.promotions}</dd>
        <dt className="text-ink-muted">Average tenure</dt><dd>{p.avg_tenure_months ?? "—"} months</dd>
        <dt className="text-ink-muted">Relevant experience</dt><dd>{e.relevant_years} years</dd>
      </dl>
      <p className="rounded-lg bg-subtle px-3 py-2 text-xs text-ink-secondary" data-testid="mobility-indicator">{p.mobility}</p>
      <div className="space-y-1 text-sm" data-testid="gap-list">
        {e.gaps.length === 0 ? <p className="text-ink-muted">No gaps longer than 2 months.</p> : e.gaps.map((g) => (
          <p key={g.from}>Gap {g.from} → {g.to}: <strong>{g.months} months</strong> <span className="text-xs text-ink-muted">{g.beyond_threshold ? `· beyond the ${e.gap_threshold_months}-month threshold (small deduction)` : "· within threshold, not penalised"}</span></p>
        ))}
        {e.overlaps.map((o) => <p key={o.from + o.roles.join()} className="text-ink-secondary">Overlap of {o.months} months from {o.from} (e.g. part-time or freelance)</p>)}
      </div>
    </section>
  );
}

const CERT_ICON = { verify_available: CheckCircle2, unverified: CircleDashed };

export function ChecklistPanel({ e }) {
  const edu = e.education_fit;
  const EduIcon = edu.status === "does_not_meet" ? XCircle : edu.status === "partial" ? CircleDashed : CheckCircle2;
  return (
    <section className="panel space-y-4 p-5" data-testid="checklist-panel">
      <h3 className="text-base font-semibold">Certifications and education</h3>
      <ul className="space-y-2 text-sm">
        {e.certifications.length === 0 && <li className="text-ink-muted">No certifications listed.</li>}
        {e.certifications.map((c) => {
          const Icon = CERT_ICON[c.status];
          return (
            <li key={c.name} className="flex flex-wrap items-center gap-2" data-testid={`cert-${c.name.toLowerCase().replace(/\W+/g, "-")}`}>
              <Icon size={15} className={c.status === "verify_available" ? "text-success" : "text-ink-muted"} />
              <span className="text-ink">{c.name}</span><span className="text-xs capitalize text-ink-muted">{c.relevance} relevance</span>
              {c.expired && <span className="rounded-full bg-caution-tint px-2 text-[11px] text-caution">Expired</span>}
              {c.verify_url ? <a href={c.verify_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-brand hover:underline">Verify <ExternalLink size={11} /></a> : <span className="text-xs text-ink-muted">Unverified</span>}
            </li>
          );
        })}
      </ul>
      <div className="flex gap-2 text-sm" data-testid="education-fit">
        <EduIcon size={15} className={cn("mt-0.5 shrink-0", edu.status === "does_not_meet" ? "text-risk" : edu.status === "partial" ? "text-caution" : "text-success")} />
        <div><p className="text-ink">Education: {edu.status.replace(/_/g, " ")}</p><p className="text-xs text-ink-secondary">{edu.rationale}</p>{edu.evidence && <p className="text-xs italic text-ink-muted">"{edu.evidence}"</p>}</div>
      </div>
      {e.achievements.length > 0 && (
        <div data-testid="achievements"><p className="mb-1 text-sm font-medium">Quantified achievements</p>
          <ul className="space-y-1 text-xs text-ink-secondary">{e.achievements.map((a) => <li key={a.quote}><span className="mr-1 rounded bg-brand-tint px-1.5 font-medium text-brand">{a.value}</span>"{a.quote}"</li>)}</ul></div>
      )}
    </section>
  );
}
